import React from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { Button, InfoMessage, TitleSubtitle } from "..";
import { RejoinQueueDialog, RejoinVariant } from "../RejoinQueueDialog/RejoinQueueDialog";
import { UserBookingInterest } from "../../models/BookingInterest";
import { getProfile, isTokenExpiredOrNull } from "../../utils/auth";
import { SecondChanceContainer, SecondChanceActions } from "./SecondChancePanel.styles";

/**
 * What an expired ticket-offer link shows instead of dead-ending (TTT-687 §6.2).
 *
 * The accept and decline pages both land here once the offer behind the token has timed
 * out. Three audiences arrive on the same URL:
 *
 *  - signed in as the booking's owner -> the rejoin CTA, straight into the usual dialog
 *  - signed out but a real account     -> log in, then back here
 *  - signed out and never signed up    -> register, then back here
 *
 * The round trip is carried by `redirectURL`, so the user returns to this exact link and
 * the rejoin is one click away rather than a hunt through the profile.
 */

/**
 * Both timed-out statuses resolve on legacy sessions too, but `RejoinQueueCommandHandler`
 * refuses them outright without the consolidated flow. So the flow flag gates the CTA:
 * without it a user on a session with the flow switched off would read the panel, tick the
 * acknowledgement, submit, and collect a hard error.
 */
export const rejoinVariantForStatus = (
  status: string | undefined,
  consolidatedFlowEnabled: boolean | undefined
): RejoinVariant | null => {
  if (!consolidatedFlowEnabled) {
    return null;
  }

  if (status === "TicketTimedOut") {
    return "offerTimedOut";
  }

  if (status === "AttendanceConfirmationTimedOutViaSMS") {
    return "checkInMissed";
  }

  return null;
};

interface SecondChancePanelProps {
  bookingInterest: UserBookingInterest;
  variant: RejoinVariant;
}

const copy = {
  offerTimedOut: {
    heading: "You missed your ticket offer",
    body: "Your ticket offer expired before it was accepted, so the seat went to the next person in the queue.",
  },
  checkInMissed: {
    heading: "You missed check-in",
    body: "Your ticket was released because check-in was not completed in time.",
  },
};

export const SecondChancePanel = ({ bookingInterest, variant }: SecondChancePanelProps) => {
  const router = useRouter();
  const texts = copy[variant];

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [completionMessage, setCompletionMessage] = React.useState("");

  // `router.asPath` keeps the token, so login/registration returns to a link that still
  // resolves to this booking.
  const returnUrl = router.asPath;

  const signedIn = !isTokenExpiredOrNull();
  const profile = signedIn ? getProfile() : null;
  // The stored profile carries no id, so the booking's owner is matched on email.
  const bookingEmail = bookingInterest.user?.email?.trim().toLowerCase();
  const isOwner = Boolean(bookingEmail && profile?.email?.trim().toLowerCase() === bookingEmail);

  // A guest whose account was only ever a stub has no password to log in with.
  const needsRegistration = bookingInterest.user?.isRegistered === false;

  // One second chance per session, and dependents travel with their lead booker.
  const alreadySpent = Boolean(bookingInterest.hasUsedSecondChance);
  const isDependent = Boolean(bookingInterest.isDependent);

  const goToLogin = () =>
    router.push({ pathname: "/login", query: { redirectURL: returnUrl } });

  const goToRegister = () =>
    router.push({
      pathname: "/register",
      query: {
        redirectURL: returnUrl,
        email: bookingInterest.user?.email ?? "",
        firstName: bookingInterest.user?.firstName ?? "",
      },
    });

  if (completionMessage) {
    return (
      <SecondChanceContainer data-cy="second-chance-success">
        <TitleSubtitle size="sm" centered subTitle={completionMessage} />
        <SecondChanceActions>
          <Link href="/profile">
            <Button full data-cy="second-chance-profile-button">
              Go to My Bookings
            </Button>
          </Link>
        </SecondChanceActions>
      </SecondChanceContainer>
    );
  }

  const renderPrimaryAction = () => {
    if (alreadySpent || isDependent) {
      return (
        <Link href="/audience">
          <Button full data-cy="second-chance-shows-button">
            Browse Upcoming Shows
          </Button>
        </Link>
      );
    }

    if (needsRegistration) {
      return (
        <Button full data-cy="second-chance-register-button" onClick={goToRegister}>
          Sign Up to Rejoin
        </Button>
      );
    }

    // Signed in as somebody else counts as signed out here: the rejoin endpoint is
    // owner-only, so send them to the right account rather than letting the call fail.
    if (!signedIn || !isOwner) {
      return (
        <Button full data-cy="second-chance-login-button" onClick={goToLogin}>
          Log In to Rejoin
        </Button>
      );
    }

    return (
      <Button full data-cy="second-chance-rejoin-button" onClick={() => setDialogOpen(true)}>
        {variant === "checkInMissed" ? "Join Last Min Queue" : "Rejoin Queue"}
      </Button>
    );
  };

  // Only the genuine dead ends get the alert treatment; the ordinary "you can rejoin" case
  // reads as prose, so the yellow banner still means something when it does appear.
  const caveat = alreadySpent
    ? "You have already used your second chance for this session."
    : isDependent
      ? "Your lead booker can rejoin the queue on behalf of your group."
      : "";

  const subTitle = caveat
    ? texts.body
    : `${texts.body} You can rejoin the queue for another chance if tickets become available.`;

  return (
    <SecondChanceContainer>
      <TitleSubtitle
        data-cy="second-chance-heading"
        size="sm"
        centered
        title={texts.heading}
        subTitle={subTitle}
      />

      {caveat && (
        <InfoMessage
          data-cy="second-chance-message"
          variant="info"
          hasBackground={true}
          message={caveat}
        />
      )}

      <SecondChanceActions>
        {renderPrimaryAction()}

        <Link href="/profile">
          <Button full variant="secondary" data-cy="go-to-bookings-button">
            Go to My Bookings
          </Button>
        </Link>
      </SecondChanceActions>

      {!alreadySpent && !isDependent && isOwner && (
        <RejoinQueueDialog
          open={dialogOpen}
          variant={variant}
          bookingInterest={bookingInterest}
          onClose={() => setDialogOpen(false)}
          onCompleted={(_successful, resultMessage) => setCompletionMessage(resultMessage)}
        />
      )}
    </SecondChanceContainer>
  );
};

SecondChancePanel.displayName = "SecondChancePanel";
